import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { collection, getDocs, getDocsFromCache, getCountFromServer, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../firebase";
import { Match, Prayer, ThankMessage } from "../types";
import { pingMatchSync } from "../utils/lolesports";

const PRAYER_LIMIT = 50;
const DAY_MS = 24 * 60 * 60 * 1000;

// The sync flags matches T1 was removed from (e.g. a re-drawn Swiss slot).
const isListed = (m: Match) => m.stale !== true;

// Match dates are stored in Vietnam time. Starting from yesterday keeps a
// late-night match that is still live after midnight.
const matchWindowStart = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Ho_Chi_Minh" }).format(
    new Date(Date.now() - DAY_MS)
  );
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const CACHE_TS_KEY = "prayerLastFetch";
const CACHE_COUNT_KEY = "prayerTotalCount";

function isCacheStale(): boolean {
  const ts = localStorage.getItem(CACHE_TS_KEY);
  const hasCount = localStorage.getItem(CACHE_COUNT_KEY);
  return !ts || !hasCount || Date.now() - parseInt(ts) > CACHE_TTL;
}

interface FirestoreData {
  matches: Match[];
  prayers: Prayer[];
  thankMessages: ThankMessage[];
  totalPrayerCount: number;
  searchResults: Prayer[] | null;
  searchPrayers: (term: string) => Promise<void>;
  clearSearch: () => void;
  refreshPrayers: () => Promise<void>;
  loadMorePrayers: () => Promise<void>;
  hasMorePrayers: boolean;
  loadingMore: boolean;
}

const FirestoreContext = createContext<FirestoreData>({
  matches: [],
  prayers: [],
  thankMessages: [],
  totalPrayerCount: 0,
  searchResults: null,
  searchPrayers: async () => {},
  clearSearch: () => {},
  refreshPrayers: async () => {},
  loadMorePrayers: async () => {},
  hasMorePrayers: false,
  loadingMore: false,
});

export const useFirestore = () => useContext(FirestoreContext);

async function fetchWithCacheFallback<T>(
  q: ReturnType<typeof query>,
  mapper: (doc: any) => T
): Promise<{ data: T[]; lastDoc: any }> {
  let snapshot;

  // Try cache first to save quota
  try {
    const cached = await getDocsFromCache(q);
    if (!cached.empty) {
      snapshot = cached;
    }
  } catch {
    // Cache miss — fall through to server
  }

  if (!snapshot) {
    snapshot = await getDocs(q);
  }

  const data: T[] = [];
  snapshot.forEach((doc) => data.push(mapper(doc)));
  const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
  return { data, lastDoc };
}

export const FirestoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [prayers, setPrayers] = useState<Prayer[]>([]);
  const [thankMessages, setThankMessages] = useState<ThankMessage[]>([]);
  const [totalPrayerCount, setTotalPrayerCount] = useState(0);
  const [searchResults, setSearchResults] = useState<Prayer[] | null>(null);
  const [hasMorePrayers, setHasMorePrayers] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastPrayerDoc = useRef<any>(null);
  const hasFetched = useRef(false);

  const docMapper = (doc: any) => {
    const data = doc.data();
    // Handle Firestore Timestamp objects for createdAt
    if (data.createdAt && typeof data.createdAt !== "number") {
      data.createdAt = data.createdAt.toMillis ? data.createdAt.toMillis() : Number(data.createdAt);
    }
    return { id: doc.id, ...data } as any;
  };

  const fetchAll = useCallback(async () => {
    if (!isFirebaseConfigured) return;

    try {
      // Only upcoming matches are shown publicly; past ones stay in Admin.
      const matchQuery = query(
        collection(db, "matches"),
        where("date", ">=", matchWindowStart()),
        orderBy("date", "asc")
      );
      const prayerQuery = query(
        collection(db, "prayers"),
        orderBy("createdAt", "desc"),
        limit(PRAYER_LIMIT)
      );
      const thankQuery = query(collection(db, "thankMessages"));

      const stale = isCacheStale();

      if (stale) {
        // Cache expired — fetch everything from server. Matches and thank
        // messages must come from the server too: the persistent cache would
        // otherwise pin returning visitors to old data forever.
        pingMatchSync();
        const [matchSnapshot, prayerSnapshot, thankSnapshot, countResult] = await Promise.all([
          getDocs(matchQuery),
          getDocs(prayerQuery),
          getDocs(thankQuery),
          getCountFromServer(query(collection(db, "prayers"))),
        ]);

        const matchData: Match[] = [];
        matchSnapshot.forEach((doc) => matchData.push(docMapper(doc)));
        const prayerData: Prayer[] = [];
        prayerSnapshot.forEach((doc) => prayerData.push(docMapper(doc)));
        const thankData: ThankMessage[] = [];
        thankSnapshot.forEach((doc) => thankData.push(docMapper(doc)));

        setMatches(matchData.filter(isListed));
        setPrayers(prayerData);
        setThankMessages(thankData);
        const totalCount = countResult.data().count;
        setTotalPrayerCount(totalCount);
        localStorage.setItem(CACHE_COUNT_KEY, String(totalCount));
        lastPrayerDoc.current = prayerSnapshot.docs[prayerSnapshot.docs.length - 1] || null;
        setHasMorePrayers(prayerData.length === PRAYER_LIMIT);
        localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
      } else {
        // Cache still fresh — use cache for everything (0 reads)
        const [matchResult, prayerResult, thankResult] = await Promise.all([
          fetchWithCacheFallback<Match>(matchQuery, docMapper),
          fetchWithCacheFallback<Prayer>(prayerQuery, docMapper),
          fetchWithCacheFallback<ThankMessage>(thankQuery, docMapper),
        ]);

        setMatches(matchResult.data.filter(isListed));
        setPrayers(prayerResult.data);
        setThankMessages(thankResult.data);
        const cachedCount = localStorage.getItem(CACHE_COUNT_KEY);
        setTotalPrayerCount(cachedCount ? parseInt(cachedCount) : prayerResult.data.length);
        lastPrayerDoc.current = prayerResult.lastDoc;
        setHasMorePrayers(prayerResult.data.length === PRAYER_LIMIT);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }, []);

  const refreshPrayers = useCallback(async () => {
    if (!isFirebaseConfigured) return;
    try {
      const q = query(
        collection(db, "prayers"),
        orderBy("createdAt", "desc"),
        limit(PRAYER_LIMIT)
      );
      // Force server fetch after submission to get the new prayer
      const snapshot = await getDocs(q);
      const data: Prayer[] = [];
      snapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Prayer);
      });
      setPrayers(data);
      lastPrayerDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
      setHasMorePrayers(data.length === PRAYER_LIMIT);
      setTotalPrayerCount((prev) => prev + 1);
      localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
    } catch (error) {
      console.error("Error refreshing prayers:", error);
    }
  }, []);

  const loadMorePrayers = useCallback(async () => {
    if (!isFirebaseConfigured || !lastPrayerDoc.current || loadingMore) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, "prayers"),
        orderBy("createdAt", "desc"),
        startAfter(lastPrayerDoc.current),
        limit(PRAYER_LIMIT)
      );
      const snapshot = await getDocs(q);
      const newPrayers: Prayer[] = [];
      snapshot.forEach((doc) => {
        newPrayers.push({ id: doc.id, ...doc.data() } as Prayer);
      });
      setPrayers((prev) => [...prev, ...newPrayers]);
      lastPrayerDoc.current = snapshot.docs[snapshot.docs.length - 1] || null;
      setHasMorePrayers(newPrayers.length === PRAYER_LIMIT);
    } catch (error) {
      console.error("Error loading more prayers:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore]);

  const searchPrayers = useCallback(async (term: string) => {
    if (!isFirebaseConfigured || !term.trim()) {
      setSearchResults(null);
      return;
    }
    const cleaned = term.trim().replace(/^@/, "");
    const q = query(
      collection(db, "prayers"),
      where("threadsUsername", ">=", cleaned),
      where("threadsUsername", "<=", cleaned + "\uf8ff"),
      limit(50)
    );
    const snapshot = await getDocs(q);
    const results: Prayer[] = [];
    snapshot.forEach((doc) => results.push({ id: doc.id, ...doc.data() } as Prayer));
    setSearchResults(results);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults(null);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchAll();
  }, [fetchAll]);

  return (
    <FirestoreContext.Provider
      value={{
        matches,
        prayers,
        thankMessages,
        totalPrayerCount,
        searchResults,
        searchPrayers,
        clearSearch,
        refreshPrayers,
        loadMorePrayers,
        hasMorePrayers,
        loadingMore,
      }}
    >
      {children}
    </FirestoreContext.Provider>
  );
};
